import { useState } from 'react';
import { Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDocumentTags } from '@/hooks/react-query/useDocumentTags';
import { DocumentTagBadge } from './DocumentTagBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DocumentTagFilterProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  filterMode?: 'any' | 'all';
  onFilterModeChange?: (mode: 'any' | 'all') => void;
  className?: string;
}

export function DocumentTagFilter({
  selectedTags,
  onTagsChange,
  filterMode = 'any',
  onFilterModeChange,
  className,
}: DocumentTagFilterProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: tagsResponse } = useDocumentTags();

  const availableTags = tagsResponse?.data || [];
  const filteredTags = availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleTag = (tagName: string) => {
    if (selectedTags.includes(tagName)) {
      onTagsChange(selectedTags.filter((t) => t !== tagName));
    } else {
      onTagsChange([...selectedTags, tagName]);
    }
  };

  const handleClearAll = () => {
    onTagsChange([]);
  };

  const selectedCount = selectedTags.length;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter by Tags
            {selectedCount > 0 && (
              <span className="ml-2 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-xs">
                {selectedCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Tags</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                disabled={selectedCount === 0}
                className="h-6 px-2 text-xs"
              >
                Clear all
              </Button>
            </div>
            <Input
              placeholder="Search tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8"
            />
            {onFilterModeChange && selectedCount > 1 && (
              <div className="mt-2">
                <Select value={filterMode} onValueChange={onFilterModeChange}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Match any tag</SelectItem>
                    <SelectItem value="all">Match all tags</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <ScrollArea className="h-[250px]">
            <div className="p-2">
              {filteredTags.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No tags found</p>
              ) : (
                <div className="space-y-1">
                  {filteredTags.map((tag) => {
                    const isSelected = selectedTags.includes(tag.name);
                    return (
                      <label
                        key={tag.id}
                        role="menuitem"
                        className={cn(
                          'flex items-center space-x-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-accent',
                          isSelected && 'bg-accent'
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleTag(tag.name)}
                          data-testid={`tag-checkbox-${tag.id}`}
                        />
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: tag.color || '#3B82F6' }}
                        />
                        <span className="flex-1">{tag.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {selectedTags.map((tag) => (
            <DocumentTagBadge
              key={tag}
              tagName={tag}
              size="sm"
              className="group"
              onClick={() => handleToggleTag(tag)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
